import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalZIndex,
  MDSModalDialog,
  MDSTextColor,
  MDSTextWeight,
} from "@/mds";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { MDSText, MDSTextVariant } from "@/mds";
import { localStorageModule } from "@/modules/local-storage";
import { useAppStore } from "@/store";
import { ModalDefinition, ModalDefinitionKind } from "@/store/modals/types";
import { noop } from "lodash-es";
import { observer } from "mobx-react-lite";
import { useCallback, useRef, useState } from "react";

export const UpgradedUserEmailChecker = observer(function UpgradedUserEmailChecker() {
  const { store } = useAppStore();

  const modalDefinitionRef = useRef<ModalDefinition>({
    kind: ModalDefinitionKind.UpgradedUserEmailMismatch,
  });

  const [upgradedUserEmail, setUpgradedUserEmail] = useState<string | null>(null);

  useEffectOnMount(() => {
    (async () => {
      const upgradedUserEmail = localStorageModule.readUpgradedUserEmail();
      setUpgradedUserEmail(upgradedUserEmail);

      const isIncompatible =
        !!upgradedUserEmail && upgradedUserEmail !== store.account.myAccount.profileEmailAddress;

      if (!isIncompatible) {
        localStorageModule.clearUpgradedUserEmail();
        return;
      }

      // Priority: we want to potentially switch the account before other long operations, e.g,
      // from "App reload required".
      store.modals.addModal(modalDefinitionRef.current, { priority: true });
    })();
  });

  const onClearMigratedUserEmail = useCallback(() => {
    localStorageModule.clearUpgradedUserEmail();
    store.modals.removeModal(modalDefinitionRef.current);
    setUpgradedUserEmail(null);
  }, [store.modals]);

  const onSignOut = useCallback(() => {
    store.auth.logOut();
  }, [store.auth]);

  return (
    <MDSModalDialog
      isOpen={store.modals.current?.kind === ModalDefinitionKind.UpgradedUserEmailMismatch}
      handleClose={noop}
      isDismissable={false}
      zIndex={MDSModalZIndex.AppStoreCrashNotice}
    >
      <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        Switch accounts to continue
      </MDSText>
      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          You&apos;re signed in with a different account than the one you used to upgrade. Sign in
          as{" "}
          <MDSText as="span" weight={MDSTextWeight.SemiBold} color={MDSTextColor.Secondary}>
            {upgradedUserEmail}
          </MDSText>{" "}
          to access your migrated content.
        </MDSText>
      </MDSDialogTextContent>
      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Default} onClick={onClearMigratedUserEmail}>
          Dismiss
        </MDSButton>
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={onSignOut}>
          Sign in as {upgradedUserEmail}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});
